import { createContext, useContext, useEffect, useMemo, useState, useCallback } from "react";
import PropTypes from "prop-types";
import { authApi, personaApi } from "@/lib/api";
import { setUnauthorizedHandler, notifySessionEnded, isPublicAuthPath, isOnboardingPath } from "@/lib/authSession";
import { clearTenantScopedClientState } from "@/lib/clearTenantScopedClientState";
import { usePersonaStore } from "@/store/usePersonaStore";
import { useOnboardingStore } from "@/store/useOnboardingStore";

const AppStoreContext = createContext(null);

const STORAGE_KEY = "sellix.auth.v1";

function persistAuth(state) {
  try {
    if (state) localStorage.setItem(STORAGE_KEY, JSON.stringify(state));
    else localStorage.removeItem(STORAGE_KEY);
  } catch {
    /* ignore */
  }
}

function computeInitials(name, email) {
  const src = (name || email || "?").trim();
  const parts = src.split(/\s+/).filter(Boolean);
  if (parts.length >= 2) return (parts[0][0] + parts[1][0]).toUpperCase();
  return src.slice(0, 2).toUpperCase();
}

function buildUserFromMe(me) {
  return {
    name: me.display_name || me.email,
    email: me.email,
    initials: computeInitials(me.display_name, me.email),
  };
}

function buildTenantFromMe(me) {
  const label = me.tenant_display_name || me.tenant_slug || "Workspace";
  return {
    id: me.tenant_id,
    name: label,
    slug: me.tenant_slug,
    workspaces: [{
      id: me.tenant_id,
      name: label,
      region: "Primary",
    }],
  };
}

function hydratePersonaFromServer() {
  personaApi.get()
    .then((p) => usePersonaStore.getState().hydrateFromServer(p))
    .catch(() => { /* persona optional until onboarding */ });
}

function hydrateOnboardingFromMe(me) {
  if (!me) return;
  useOnboardingStore.getState().hydrateFromServer(me);
}

function authFromMe(me) {
  const tenant = buildTenantFromMe(me);
  return {
    stage: "authed",
    user: buildUserFromMe(me),
    tenantId: me.tenant_id,
    workspaceId: me.tenant_id,
    token: "session",
    server: {
      user_id: me.user_id,
      tenant_slug: me.tenant_slug,
      tenant_display_name: me.tenant_display_name,
      plan: me.plan,
      mfa_enabled: me.mfa_enabled,
      role: me.role || "owner",
      onboarding_completed: Boolean(me.onboarding_completed),
      onboarding_completed_at: me.onboarding_completed_at || null,
      onboarding_step: me.onboarding_step || null,
    },
    tenant,
    workspace: tenant.workspaces[0],
  };
}

export function AppStoreProvider({ children }) {
  const [auth, setAuth] = useState(null);
  const [authReady, setAuthReady] = useState(false);
  const [sidebarCollapsed, setSidebarCollapsed] = useState(false);
  const [paletteOpen, setPaletteOpen] = useState(false);
  const [mfa, setMfa] = useState({ enabled: false, verified_at: null, backup_codes: [] });

  useEffect(() => {
    persistAuth(auth);
  }, [auth]);

  const refreshAuth = useCallback(async () => {
    try {
      const me = await authApi.me();
      hydrateOnboardingFromMe(me);
      setAuth(authFromMe(me));
      setMfa((m) => ({ ...m, enabled: !!me.mfa_enabled }));
      if (!isOnboardingPath()) {
        hydratePersonaFromServer();
      }
      return true;
    } catch (err) {
      if (err?.status === 401) setAuth(null);
      return false;
    }
  }, []);

  useEffect(() => {
    setUnauthorizedHandler(() => {
      clearTenantScopedClientState();
      setAuth(null);
      if (!isPublicAuthPath()) notifySessionEnded();
    });
    return () => setUnauthorizedHandler(null);
  }, []);

  useEffect(() => {
    let cancelled = false;
    refreshAuth().finally(() => {
      if (!cancelled) setAuthReady(true);
    });
    const onFocus = () => { refreshAuth(); };
    window.addEventListener("focus", onFocus);
    return () => {
      cancelled = true;
      window.removeEventListener("focus", onFocus);
    };
  }, [refreshAuth]);

  const signIn = useCallback(async (email, password) => {
    clearTenantScopedClientState();
    const r = await authApi.login({ email, password });
    if (r?.mfa_required) return { mfaRequired: true, challengeId: r.challenge_id };
    const me = await authApi.me();
    hydrateOnboardingFromMe(me);
    setAuth(authFromMe(me));
    hydratePersonaFromServer();
    return { mfaRequired: false, onboardingCompleted: Boolean(me.onboarding_completed) };
  }, []);

  const signUp = useCallback(async ({ email, password, display_name, tenant_slug }) => {
    clearTenantScopedClientState();
    await authApi.register({ email, password, display_name, tenant_slug });
    const me = await authApi.me();
    hydrateOnboardingFromMe(me);
    setAuth(authFromMe(me));
    hydratePersonaFromServer();
  }, []);

  const completeMfa = useCallback(async (challengeId, code) => {
    clearTenantScopedClientState();
    await authApi.mfaChallenge(challengeId, code);
    const me = await authApi.me();
    hydrateOnboardingFromMe(me);
    setAuth(authFromMe(me));
    return { onboardingCompleted: Boolean(me.onboarding_completed) };
  }, []);

  const signOut = useCallback(async () => {
    try { await authApi.logout(); } catch { /* ignore */ }
    clearTenantScopedClientState();
    setAuth(null);
    notifySessionEnded();
  }, []);

  const isAuthed = authReady && auth?.stage === "authed";

  const tenant = useMemo(
    () => auth?.tenant || (auth?.tenantId ? buildTenantFromMe({
      tenant_id: auth.tenantId,
      tenant_slug: auth.server?.tenant_slug,
      tenant_display_name: auth.server?.tenant_display_name,
    }) : null),
    [auth],
  );

  const workspace = useMemo(
    () => auth?.workspace || tenant?.workspaces?.[0] || null,
    [auth?.workspace, tenant],
  );

  const switchWorkspace = useCallback((workspaceId) => {
    setAuth((prev) => {
      if (!prev) return prev;
      const nextWorkspace = prev.tenant?.workspaces?.find((w) => w.id === workspaceId)
        || prev.workspace;
      return { ...prev, workspaceId, workspace: nextWorkspace };
    });
  }, []);

  const value = useMemo(
    () => ({
      auth,
      authReady,
      isAuthed,
      user: auth?.user || null,
      tenant,
      workspace,
      tenants: tenant ? [tenant] : [],
      sidebarCollapsed,
      paletteOpen,
      mfa,
      setMfa,
      setSidebarCollapsed,
      setPaletteOpen,
      signIn,
      signUp,
      completeMfa,
      switchWorkspace,
      signOut,
      refreshAuth,
    }),
    [auth, authReady, isAuthed, tenant, workspace, sidebarCollapsed, paletteOpen, mfa, signIn, signUp, completeMfa, switchWorkspace, signOut, refreshAuth],
  );

  return <AppStoreContext.Provider value={value}>{children}</AppStoreContext.Provider>;
}

export function useAppStore() {
  const ctx = useContext(AppStoreContext);
  if (!ctx) throw new Error("useAppStore must be used inside AppStoreProvider");
  return ctx;
}

AppStoreProvider.propTypes = {
  children: PropTypes.node.isRequired,
};
